import { type ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core'
import { type HubConfiguration, HubConfigurationModule, HubConfigurationService, HubStaticConfiguration } from '@inside-hub-app/hub-configuration'
import { PpMissingTranslationHandler, PpModule, ENVIRONMENT, type PpModuleConfig, PpTranslationLoader, STORAGE_PREFIX, TranslationService, TRANSLATION_PREFIX } from '@inside-hub-app/ef-ng-pp-client'
import { NGXLogger } from 'ngx-logger'
import { TxNativeModule } from '@emilfreydigital/transifex-angular'
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { HubTranslatePipe } from './hub-translate.pipe'
import { HubSharedTranslateModule } from './hub-shared-translate.module'

const appConfig: HubConfiguration = HubStaticConfiguration.get()

export function HttpLoaderFactory (
  translationService: TranslationService,
  hubConfigurationService: HubConfigurationService
): PpTranslationLoader {
  if (window) {
    const params = new URL(window.location.href).searchParams
    if (params) {
      const showTranslationKeys = params.get('showTranslationKeys')
      if (showTranslationKeys === 'true') {
        return new PpTranslationLoader(
          [{ type: '', prefix: '' }],
          '',
          translationService
        )
      }
    }
  }

  return new PpTranslationLoader(
    hubConfigurationService.configuration.translations.types,
    '',
    translationService,
    hubConfigurationService.configuration.translations.fallbackLanguages
  )
}

/**
 * This module exports the PpModule, TranslateModule (ngx-translate) and the TxNativeModule (transifex).
 * Always use the forRoot method to import the module.
 */
@NgModule({
  imports: [
    HubConfigurationModule,
    PpModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [TranslationService, HubConfigurationService]
      },
      useDefaultLang: true,
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: PpMissingTranslationHandler
      }
    }),
    TxNativeModule.forRoot(),
    HubSharedTranslateModule
  ],
  declarations: [],
  exports: [
    TranslateModule,
    PpModule,
    TxNativeModule,
    HubTranslatePipe,
    HubSharedTranslateModule
  ]
})
export class HubRootTranslateModule {
  constructor (
    @Optional() @SkipSelf() private readonly parentModule?: HubRootTranslateModule,
    @Optional() private readonly hubConfigurationService?: HubConfigurationService,
    @Optional() private readonly logger?: NGXLogger
  ) {
    if (parentModule) {
      throw new Error(
        'HubRootTranslateModule imported twice. Feature modules should use HubChildTranslateModule import instead.'
      )
    }
    if (this.hubConfigurationService && this.logger) {
      this.hubConfigurationService.setLoggerLevel(this.logger)
    }
  }

  /**
   *
   * @param config The PpModule config. If not provided, the one from configuration.services.pp will be taken.
   * @returns The module with providers.
   */
  public static forRoot (config?: PpModuleConfig): ModuleWithProviders<HubRootTranslateModule> {
    return {
      ngModule: HubRootTranslateModule,
      providers: [{
        provide: ENVIRONMENT,
        useValue: config?.environment ?? appConfig.services.pp.environment
      }, {
        provide: STORAGE_PREFIX,
        useValue: config?.storagePrefix ?? appConfig.services.pp.storagePrefix
      }, {
        provide: TRANSLATION_PREFIX,
        useValue: config?.translationPrefix ?? appConfig.services.pp.translationPrefix
      }]
    }
  }
}
